import {
  Button,
  Checkbox,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";
import { useNavigate } from "react-router-dom";
import AuthLayout from "./AuthLayout";
import { VisibilityOff, Visibility } from "@mui/icons-material";
import React from "react";
import { useLogin } from "../../api/hooks/useAuth";
import { useAuthActions } from "../../state/hooks";
import { showSuccess, showError } from "../../helpers/toast";
import { save, loadString } from "../../helpers/storage";
import { STORAGE_KEYS } from "../../constants/storage";

const Login = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = React.useState(false);
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [rememberMe, setRememberMe] = React.useState(false);

  const { setUser, setToken } = useAuthActions();

  const { mutate: login, isPending: isLoading } = useLogin({
    onSuccess: (response) => {
      // Handle different response structures
      const token =
        response.data?.token ||
        response.data?.user?.token ||
        response.data?.accessToken ||
        (typeof response.data === 'object' && response.data !== null ? (response.data as any).token : null);

      const user =
        response.data?.user ||
        response.data?.data?.user ||
        (response.data && typeof response.data === 'object' && !response.data.token ? response.data : null);

      if (token) {
        setToken(token);
        // Don't set user from login response - let Topbar fetch full profile with role/permissions
        // This ensures we always have complete user data including role and permissionId
        save(STORAGE_KEYS.token, token);
        showSuccess(response.message || "Login successful");
        navigate("/dashboard");
      } else {
        showError("Token not received from server");
      }
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Login failed. Please check your credentials.";
      showError(errorMessage);
    },
  });

  const handleClickShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      showError("Please enter both email and password");
      return;
    }

    const deviceToken = (await loadString(STORAGE_KEYS.fcmToken)) || "abc";

    login({
      email,
      password,
      deviceType: "WEB",
      deviceToken,
      rememberMe,
    });
  };

  return (
    <AuthLayout>
      <div className="authBox">
        <h2>Login</h2>

        <form className="form" onSubmit={handleSubmit}>
          <div className="control_group">
            <label htmlFor="email">Email Address</label>
            <TextField
              id="email"
              name="email"
              type="email"
              placeholder="Enter Email Address"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isLoading}
            />
          </div>

          <div className="control_group">
            <label htmlFor="password">Password</label>
            <TextField
              id="password"
              name="password"
              placeholder="Enter Password"
              fullWidth
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isLoading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" className="eye_btn">
                    <IconButton
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </div>

          <div className="control_group d_flex">
            <div className="remember_box">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    disabled={isLoading}
                  />
                }
                name="remember"
                label="Remember me"
              />
            </div>
            <button
              type="button"
              className="anchor_link"
              onClick={() => navigate("/forgot-password")}
              disabled={isLoading}
            >
              Forgot Password?
            </button>
          </div>

          <div className="form_btn">
            <Button
              type="submit"
              className="btn btn_primary w_100"
              variant="contained"
              disabled={isLoading}
            >
              {isLoading ? "Logging in..." : "Login"}
            </Button>
          </div>
        </form>
      </div>
    </AuthLayout>
  );
};

export default Login;
